import { useEffect, useState } from 'react';
import { AlertTriangle, CalendarClock, Loader2 } from 'lucide-react';
import type { BriefDefinition, BriefProposal } from '../../types/briefs';
import { briefsApi } from '../../services/briefsApi';
import { draftErrorMessage, draftScheduleSummary, missingText } from '../../utils/briefPresentation';
import { RunView } from '../../tabs/ZoeBriefs';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';

type Outcome =
  | { kind: 'idle' }
  | { kind: 'busy'; action: 'create' | 'cancel' }
  | { kind: 'created'; brief: BriefDefinition }
  | { kind: 'cancelled' }
  | { kind: 'error'; message: string };

/**
 * What the server says about the draft now. The card's proposal is a snapshot
 * from the turn that drew it, and Zoe's history replays old turns, so a card
 * can look open long after its draft was created, cancelled or revised.
 */
type Current = 'checking' | 'open' | 'superseded' | 'closed' | 'unknown';

/**
 * A brief Zoe drafted (docs/architecture/zoe_curated_briefs.md §12.7): what it
 * gathers, when it runs, and the brief filled with data from when it was
 * drafted, so the user sees what they are creating.
 * Zoe cannot create briefs; Create here is the user's own request, and it
 * names the draft revision this card shows, so the server refuses it if the
 * draft has changed rather than creating something else.
 */
export default function BriefProposalCard({
  proposal: snapshot,
  onOpenBriefs,
  onCreated,
  onCancelled,
}: {
  proposal: BriefProposal;
  /** Omitted outside the app shell; the link is then not offered. */
  onOpenBriefs?: () => void;
  onCreated?: (brief: BriefDefinition) => void;
  onCancelled?: () => void;
}) {
  const [p, setP] = useState<BriefProposal>(snapshot);
  useEffect(() => setP(snapshot), [snapshot]);
  const [current, setCurrent] = useState<Current>(snapshot.state === 'open' ? 'checking' : 'closed');
  useEffect(() => {
    if (snapshot.state !== 'open') return void setCurrent('closed');
    let live = true;
    setCurrent('checking');
    Promise.resolve()
      .then(() => briefsApi.getDraft(snapshot.conversation_id))
      .then((d) => {
        if (!live) return;
        if (d.draft_id !== snapshot.draft_id || d.revision !== snapshot.revision) return void setCurrent('superseded');
        setP(d);
        setCurrent(d.state === 'open' ? 'open' : 'closed');
      })
      .catch(() => { if (live) setCurrent('unknown'); });
    return () => { live = false; };
  }, [snapshot]);
  const [outcome, setOutcome] = useState<Outcome>(() =>
    snapshot.state === 'cancelled' ? { kind: 'cancelled' } : { kind: 'idle' },
  );
  const busy = outcome.kind === 'busy';
  const notShown = p.not_shown ?? [];
  // A brief that misses part of what was asked must not look finished: Create
  // waits until the user says it is still wanted without those parts.
  const [acceptedGaps, setAcceptedGaps] = useState(false);

  const act = async (action: 'create' | 'cancel') => {
    setOutcome({ kind: 'busy', action });
    try {
      if (action === 'create') {
        const brief = await briefsApi.confirmDraft(p);
        setOutcome({ kind: 'created', brief });
        onCreated?.(brief);
      } else {
        await briefsApi.cancelDraft(p);
        setOutcome({ kind: 'cancelled' });
        onCancelled?.();
      }
    } catch (err) {
      setOutcome({ kind: 'error', message: draftErrorMessage(err) });
    }
  };

  const superseded = current === 'superseded';
  const closed = outcome.kind === 'created' || outcome.kind === 'cancelled' || p.state !== 'open' || superseded;
  const canCreate = p.ready && !closed && !busy && (notShown.length === 0 || acceptedGaps);
  const kind = p.alert ? 'alert' : 'brief';
  // Only a draft the server confirms is still open, ready and unactioned is worth a warning on leaving.
  const unsaved = current === 'open' && p.ready && !closed;
  useUnsavedChanges(
    `brief-draft:${p.draft_id}`,
    unsaved ? `Your ${kind} "${p.name}" hasn't been created yet. Leave without creating it? You can still create it from the Briefs page for 24 hours.` : null,
  );
  const now = p.preview?.alert;

  return (
    <section
      aria-label={`Draft ${kind}: ${p.name}`}
      className="rounded-lg border border-success/40 bg-card/40 px-4 py-3 text-sm text-foreground space-y-3"
    >
      <header className="flex items-center gap-2">
        <CalendarClock className="w-4 h-4 text-success" aria-hidden />
        <span className="font-medium">{p.name}</span>
        <span className="ml-auto text-xs text-muted-foreground">Draft {kind}</span>
      </header>

      <dl className="grid grid-cols-auto-1fr gap-x-3 gap-y-1">
        {p.gathers ? (
          <>
            <dt className="text-muted-foreground">Gathers</dt>
            <dd>{p.gathers}</dd>
          </>
        ) : null}
        <dt className="text-muted-foreground">Sections</dt>
        <dd>
          {p.sections.length > 0 ? p.sections.map((s) => s.heading).join(', ') : <span className="text-muted-foreground">None yet</span>}
        </dd>
        <dt className="text-muted-foreground">When</dt>
        <dd>{draftScheduleSummary(p.schedule)}</dd>
        {p.alert ? (
          <>
            <dt className="text-muted-foreground">Sends</dt>
            <dd>
              {p.alert.condition
                ? `Only ${p.alert.condition}`
                : <span className="text-warning">Only when {p.alert.when} — not yet something the data can check</span>}
            </dd>
            {now ? (
              <>
                <dt className="text-muted-foreground">Right now</dt>
                <dd>
                  {now.unavailable
                    ? <span className="text-muted-foreground">Couldn&apos;t check on today&apos;s data: {now.unavailable}</span>
                    : now.would_send ? 'Would send' : 'Would not send'}
                  {now.message ? <span className="block text-foreground">“{now.message}”</span> : null}
                </dd>
              </>
            ) : null}
          </>
        ) : null}
      </dl>

      {notShown.length > 0 ? (
        <div role="note" aria-label={`Not in this ${kind}`} className="rounded border border-amber-600/60 bg-amber-900/20 px-3 py-2 space-y-1">
          <p className="font-medium text-amber-200">Not in this {kind}</p>
          <ul className="space-y-1 text-amber-100">
            {notShown.map((n) => <li key={n.part}>{n.part}: {n.reason}</li>)}
          </ul>
          {p.ready && !closed ? (
            <label className="flex items-center gap-2 text-gray-200">
              <input type="checkbox" checked={acceptedGaps} onChange={(e) => setAcceptedGaps(e.target.checked)} />
              Create it without these
            </label>
          ) : null}
        </div>
      ) : null}
      {p.questions.length > 0 ? (
        <ul aria-label="Questions" className="space-y-1 text-warning">
          {p.questions.map((q) => <li key={q}>{q}</li>)}
        </ul>
      ) : null}
      {p.notes.length > 0 ? (
        <ul aria-label="Notes" className="space-y-1 text-foreground">
          {p.notes.map((n) => <li key={n}>{n}</li>)}
        </ul>
      ) : null}
      {p.preview && p.state === 'open' ? (
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">With data from when it was drafted; each run will use fresh data.</p>
          <RunView
            saved={false}
            run={{ run_id: 'preview', brief_id: '', definition_revision: 0, trigger: 'preview', executed_at: p.preview.executed_at, status: p.preview.status, sections: p.preview.sections }}
            timezone={p.schedule.timezone}
          />
        </div>
      ) : null}
      {!p.ready && p.state === 'open' ? (
        <p className="text-foreground">Still needed: {missingText(p.missing)}. Tell Zoe below.</p>
      ) : null}

      {outcome.kind === 'created' ? (
        <p role="status" className="text-success">
          {p.alert ? 'Alert' : 'Brief'} created.{' '}
          {onOpenBriefs ? (
            <button type="button" className="underline" onClick={onOpenBriefs}>Open Briefs</button>
          ) : null}
        </p>
      ) : outcome.kind === 'cancelled' ? (
        <p role="status" className="text-muted-foreground">Draft cancelled.</p>
      ) : superseded ? (
        <p role="status" className="text-muted-foreground">Zoe changed this draft since; the latest version is further down.</p>
      ) : p.state === 'confirmed' ? (
        <p role="status" className="text-muted-foreground">This draft was already used to create a brief.</p>
      ) : p.state === 'expired' ? (
        <p role="status" className="text-muted-foreground">This draft has expired. Ask Zoe for a new one.</p>
      ) : outcome.kind === 'error' ? (
        <p role="alert" className="text-destructive">{outcome.message}</p>
      ) : null}

      {!closed && p.ready ? (
        <p role="note" className="flex items-start gap-1.5 rounded border border-amber-500/70 bg-amber-900/30 px-3 py-2 font-medium text-amber-100">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-300" aria-hidden />
          <span>
            Not saved yet. This {kind} won&apos;t exist or run until you press Create {kind}.
          </span>
        </p>
      ) : null}
      {!closed ? (
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={!canCreate}
            onClick={() => void act('create')}
            className="rounded bg-success px-3 py-1.5 text-white disabled:opacity-40"
          >
            Create {kind}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void act('cancel')}
            className="rounded border border-accent px-3 py-1.5 text-foreground disabled:opacity-40"
          >
            Cancel
          </button>
          {busy ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" aria-label="Working" /> : null}
          <span className="ml-auto text-xs text-muted-foreground">Kept as a draft for 24 hours, also listed on the Briefs page.</span>
        </div>
      ) : null}
    </section>
  );
}
